import { useCallback, useEffect, useMemo, useState } from "react";
import { applyRows, groupRidesBySection, planMove, resolveDrop } from "../util/sectionGestures";
import { queueRideSubsections } from "../local/rideSubsections";

// #3293 - the scribe's view of an equitation / medal class's sections: the
// rides with their section fields (optimistically updated by a gesture), the
// grouping, the remembered current tab, and the gesture entry points.
//
// A gesture writes the cache + queues the outbox op (local/rideSubsections.js)
// and ALSO keeps its rows in `local` so the grid reflects it before
// useRidesForClass re-reads. `local` is dropped whenever the hook hands over a
// new `rides` array: that read goes through the same overlay as the cache, so
// it already carries the gesture.

export const POOL_TAB = "pool";
const storageKey = (classId) => `scribe.sectionTab.${classId}`;

function readTab(classId) {
  try {
    return window.localStorage.getItem(storageKey(classId));
  } catch {
    return null;
  }
}
function writeTab(classId, value) {
  try {
    window.localStorage.setItem(storageKey(classId), String(value));
  } catch {
    // per-device convenience only
  }
}

export function useClassSections({ classId, rides, enabled }) {
  const [local, setLocal] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(() => (classId ? readTab(classId) : null));

  useEffect(() => {
    setLocal(null);
  }, [rides]);
  useEffect(() => {
    setTab(classId ? readTab(classId) : null);
  }, [classId]);

  const effectiveRides = useMemo(
    () => (enabled && local ? applyRows(rides, local) : rides),
    [enabled, local, rides],
  );
  const groups = useMemo(() => groupRidesBySection(effectiveRides), [effectiveRides]);

  // The current tab: the remembered one while it still exists, else the first
  // section, else Not drawn.
  const current = useMemo(() => {
    const numbers = groups.sections.map((s) => s.number);
    const asNumber = Number(tab);
    if (tab && tab !== POOL_TAB && numbers.includes(asNumber)) return asNumber;
    if (tab === POOL_TAB) return POOL_TAB;
    return numbers.length ? numbers[0] : POOL_TAB;
  }, [tab, groups.sections]);

  const selectTab = useCallback(
    (value) => {
      setTab(String(value));
      if (classId) writeTab(classId, value);
    },
    [classId],
  );

  const ship = useCallback(
    async (primaryRideId, rows) => {
      if (!rows.length) return false;
      setError(null);
      try {
        await queueRideSubsections({ primaryRideId, rows });
        setLocal((prev) => {
          const merged = new Map((prev ?? []).map((r) => [r.rideId, r]));
          for (const r of rows) merged.set(r.rideId, r);
          return [...merged.values()];
        });
        return true;
      } catch (e) {
        setError(e);
        return false;
      }
    },
    [],
  );

  // A drop (pointer or keyboard). Follows a rider to the tab it was dropped on.
  const drop = useCallback(
    async (activeId, overId) => {
      const result = resolveDrop(effectiveRides, activeId, overId);
      if (!result) return null;
      const ok = await ship(activeId, result.rows);
      if (ok && result.kind !== "reorder") {
        const moved = result.rows.find((r) => r.rideId === activeId);
        if (moved?.subsection) selectTab(moved.subsection);
      }
      return ok ? result : null;
    },
    [effectiveRides, ship, selectTab],
  );

  // "Add riders" picker: draw several riders into one section ("new" when
  // there is none yet).
  const addRiders = useCallback(
    async (rideIds, target) => {
      if (!rideIds.length) return false;
      const rows = planMove(effectiveRides, rideIds, target);
      const ok = await ship(rideIds[0], rows);
      if (ok) {
        const first = rows.find((r) => r.rideId === rideIds[0]);
        if (first?.subsection) selectTab(first.subsection);
      }
      return ok;
    },
    [effectiveRides, ship, selectTab],
  );

  return { effectiveRides, groups, current, selectTab, drop, addRiders, error };
}
